import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FaMapMarkerAlt } from 'react-icons/fa';
import projects from '../data/projects';

const filters = ['All', 'Completed', 'Ongoing'];

const imageUrl = (path) => `${process.env.PUBLIC_URL}${path}`;

const ProjectSection = ({ limit, showFilters = true, showHeading = true }) => {
    const [filter, setFilter] = useState('All');
    const [selected, setSelected] = useState(null);
    const [activeImage, setActiveImage] = useState(0);

    const filteredProjects = filter === 'All'
        ? projects
        : projects.filter((project) => project.status === filter);
    const visibleProjects = limit ? projects.slice(0, limit) : filteredProjects;

    const openProject = (project) => {
        setActiveImage(0);
        setSelected(project);
    };

    const closeProject = () => setSelected(null);

    const showImage = (index) => {
        if (!selected) return;
        const total = selected.images.length;
        setActiveImage((index + total) % total);
    };

    return (
        <section className="page-section project-section" id="projects">
            <div className="container">
                {showHeading && (
                    <div className="row align-items-end g-4 mb-4 reveal">
                        <div className="col-lg-5">
                            <p className="eyebrow">Portfolio</p>
                            <h2 className="section-title">Our projects</h2>
                        </div>
                        <div className="col-lg-7">
                            <p className="section-lead">
                                facade, glazing, cladding, and interior works across Delhi NCR — from completed towers to projects still on site.
                            </p>
                        </div>
                    </div>
                )}

                {showFilters && (
                <div className="d-flex justify-content-center flex-wrap gap-2 mb-4">
                    {filters.map((item) => {
                        const count = item === 'All'
                            ? projects.length
                            : projects.filter((project) => project.status === item).length;
                        return (
                            <button
                                key={item}
                                type="button"
                                className={`project-filter ${filter === item ? 'is-active' : ''}`}
                                onClick={() => setFilter(item)}
                                aria-pressed={filter === item}
                            >
                                {item}
                                <span>{count}</span>
                            </button>
                        );
                    })}
                </div>
                )}

                <div className="row g-4">
                    {visibleProjects.map((project, index) => (
                        <div key={project.id} className="col-12 col-md-6 col-lg-4">
                            <article className="project-card tilt-3d h-100 reveal" style={{ transitionDelay: `${(index % 3) * 90}ms` }}>
                                <button
                                    type="button"
                                    className="project-card-hit"
                                    onClick={() => openProject(project)}
                                >
                                    <div className="project-card-media">
                                        <img
                                            src={imageUrl(project.images[0])}
                                            alt={project.title}
                                        />
                                        <span className={`project-status is-${project.status.toLowerCase()}`}>
                                            {project.status}
                                        </span>
                                    </div>
                                    <div className="project-card-body">
                                        <div className="project-work-type">{project.workType}</div>
                                        <h3>{project.title}</h3>
                                        <div className="project-location">
                                            <FaMapMarkerAlt aria-hidden="true" />
                                            <span>{project.location}</span>
                                        </div>
                                        <p>{project.excerpt}</p>
                                        <span className="project-card-link">View project</span>
                                    </div>
                                </button>
                            </article>
                        </div>
                    ))}
                </div>
            </div>

            <Modal
                show={Boolean(selected)}
                onHide={closeProject}
                size="xl"
                centered
                scrollable
                contentClassName="project-modal"
            >
                {selected && (
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title as="h2" className="h5 mb-0 fw-bold">
                                {selected.title}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="row g-4">
                                <div className="col-lg-6">
                                    <div className="project-gallery">
                                        <img
                                            src={imageUrl(selected.images[activeImage])}
                                            alt={`${selected.title} photo ${activeImage + 1}`}
                                        />
                                        {selected.images.length > 1 && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="project-gallery-nav is-prev"
                                                    onClick={() => showImage(activeImage - 1)}
                                                    aria-label="Previous photo"
                                                >
                                                    ‹
                                                </button>
                                                <button
                                                    type="button"
                                                    className="project-gallery-nav is-next"
                                                    onClick={() => showImage(activeImage + 1)}
                                                    aria-label="Next photo"
                                                >
                                                    ›
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {selected.images.length > 1 && (
                                        <div className="project-thumbs">
                                            {selected.images.map((image, index) => (
                                                <button
                                                    key={image}
                                                    type="button"
                                                    className={index === activeImage ? 'is-active' : ''}
                                                    onClick={() => setActiveImage(index)}
                                                    aria-label={`Show photo ${index + 1}`}
                                                >
                                                    <img src={imageUrl(image)} alt="" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="col-lg-6">
                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                        <span className={`project-status is-inline is-${selected.status.toLowerCase()}`}>
                                            {selected.status}
                                        </span>
                                        <span className="project-work-type mb-0">{selected.workType}</span>
                                    </div>
                                    <div className="project-location mb-3">
                                        <FaMapMarkerAlt aria-hidden="true" />
                                        <span>{selected.location}</span>
                                    </div>
                                    {selected.sections.map((section) => (
                                        <div key={section.title} className="project-detail-block">
                                            <h3>{section.title}</h3>
                                            {section.text && <p>{section.text}</p>}
                                            {section.items && (
                                                <ul>
                                                    {section.items.map((item) => (
                                                        <li key={item}>{item}</li>
                                                    ))}
                                                </ul>
                                            )}
                                            {section.tags && (
                                                <div className="project-tags">
                                                    {section.tags.map((tag) => (
                                                        <span key={tag}>{tag}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Modal.Body>
                    </>
                )}
            </Modal>
        </section>
    );
};

export default ProjectSection;
